/**
 * How the cleaning board renders a row (CAP-412).
 *
 * Pure formatting and per-kind copy, kept out of the page so it can be
 * exercised directly - every one of these has a boundary that was wrong at
 * least once: midnight in the property's zone, a timezone string that throws,
 * a day that is "Today".
 *
 * **None of this decides anything.** Which rows exist, what order they are in
 * and whether a job may be completed all arrive already decided from admin-api.
 * What lives here is what a thing is CALLED and how a date is spelled.
 */

import type {
  CleaningActionItem,
  CleaningActionKind,
  CleaningConfirmationPending,
  CleaningItemStatus,
  CleaningJob,
} from '@/api/client'

/** Cape Cod. Used only when a property carries no timezone of its own. */
export const DEFAULT_PROPERTY_TIME_ZONE = 'America/New_York'

export type CleaningTone = 'destructive' | 'warning' | 'success' | 'secondary'

export interface CleaningKindMeta {
  label: string
  /**
   * The one line that used to be repeated down a per-row Detail column.
   *
   * It was never row-specific: across 553 `awaiting_ack` rows admin-api's
   * `detail` took two distinct values, and `crew_heads_up`'s variants were the
   * timestamp already shown in the Day and Time columns. Said once per group it
   * is context; said on every row it was noise crowding out the columns that do
   * vary.
   */
  why: string
  tone: CleaningTone
}

/**
 * Keyed by admin-api's `CleaningActionKind`, and iterated in ITS order.
 *
 * That order is the answer to "what do I do next" and belongs to the backend,
 * so the board never re-ranks: it groups, which is free, because the rows
 * already arrive in this order.
 */
export const CLEANING_KIND_META: Record<CleaningActionKind, CleaningKindMeta> = {
  pinned: {
    label: 'Pinned',
    why: 'Flagged by you. They stay here until you unpin them.',
    tone: 'secondary',
  },
  block_needs_decision: {
    label: 'Needs a decision',
    why: 'Blocked in Uplisting, and nobody has said whether it needs a cleaning.',
    tone: 'destructive',
  },
  assign_team: {
    label: 'Needs a crew',
    why: 'Confirmed, with no crew assigned and no property default to fall back on.',
    tone: 'destructive',
  },
  paying_today_never_acked: {
    label: 'Paying today, never acked',
    why: 'Pays at 4 PM today. Mark it not required if the cleaning is not happening.',
    tone: 'destructive',
  },
  payable_problem: {
    label: 'Payable problem',
    why: 'No agreed rate for this crew at this property, so no payable can be built.',
    tone: 'destructive',
  },
  crew_heads_up: {
    label: 'Changed',
    why: 'Confirmed or changed since the crew last acked it. For a cleaning that is off, until they have been told.',
    tone: 'warning',
  },
  awaiting_ack: {
    label: 'Waiting on an ack',
    why: 'Assigned, but the crew has not confirmed they are coming.',
    tone: 'warning',
  },
  completed_but_cancelled: {
    label: 'Completed, then cancelled',
    why: 'Cleaned and paid for, then the booking cancelled.',
    tone: 'warning',
  },
}

/** What the Upcoming tab calls each substate. */
export const CLEANING_SUBSTATE_META: Record<
  NonNullable<CleaningJob['substate']>,
  { label: string; tone: CleaningTone }
> = {
  needs_decision: { label: 'Needs a decision', tone: 'destructive' },
  needs_team: { label: 'Needs a crew', tone: 'destructive' },
  needs_ack: { label: 'No ack', tone: 'warning' },
  tentative: { label: 'Tentative', tone: 'secondary' },
  ready: { label: 'Acked', tone: 'success' },
}

const HOUR_MS = 60 * 60 * 1000

/**
 * "in 45 minutes", "in 23 hours", "in 12 days".
 *
 * Hours up to two days, because "in 1 day" for something 30 hours away hides
 * whether it lands this afternoon or tomorrow night, and those are different
 * answers for a crew.
 */
function untilPhrase(ms: number): string {
  const minutes = Math.max(1, Math.round(ms / 60_000))
  if (minutes < 60) return `in ${minutes} minute${minutes === 1 ? '' : 's'}`
  const hours = Math.round(ms / HOUR_MS)
  if (hours < 48) return `in ${hours} hour${hours === 1 ? '' : 's'}`
  const days = Math.round(ms / (24 * HOUR_MS))
  return `in ${days} days`
}

/**
 * Why a Tentative badge is tentative, and how long until it confirms (CAP-794).
 *
 * Both facts are admin-api's (`confirmation_pending`); only the countdown is
 * worked out here, against the viewer's clock, because it changes every minute
 * the page is open. Null when admin-api sent nothing to explain - a job written
 * before the field existed - so the badge shows no tooltip rather than a guess.
 */
export function tentativeExplanation(
  pending: CleaningConfirmationPending | null | undefined,
  timeZone: string,
  now: Date = new Date()
): string | null {
  if (!pending) return null
  if (pending.reason === 'unknown_checkin' || pending.reason === 'unknown_booking_time') {
    const missing = pending.reason === 'unknown_checkin' ? 'check-in' : 'booking'
    return `The ${missing} time is unknown, so this will not confirm on its own. Press Confirm on the job once the stay is real.`
  }
  const confirmsMs = Date.parse(pending.confirms_at ?? '')
  if (!Number.isFinite(confirmsMs)) return null

  const iso = new Date(confirmsMs).toISOString()
  const day = formatCleaningDay(localDate(iso, timeZone), localDate(now.toISOString(), timeZone))
    // "today" mid-sentence, not "Today": the table cell's capital is a heading's.
    .replace(/^(Today|Tomorrow)$/, name => name.toLowerCase())
  const when = `${day} at ${shortTime(iso, timeZone)}`
  const why =
    pending.reason === 'free_cancel_window'
      ? 'The guest can still cancel for free.'
      : 'Check-in is more than 30 days away.'
  const remainingMs = confirmsMs - now.getTime()
  // The half-hourly check is what actually confirms it, so a time just passed
  // is "any minute now" rather than a negative countdown.
  if (remainingMs <= 0) return `${why} Confirming within the next half hour.`
  return `${why} Confirms ${when}, ${untilPhrase(remainingMs)}.`
}

/**
 * What a FINISHED cleaning's state cell says (CAP-526).
 *
 * A substate only exists for a job that is still scheduled - it describes what
 * the job is waiting for - so a completed or cancelled one has none. While this
 * board showed open jobs only, that gap was invisible; the All-cleanings tab
 * reaches back over hundreds of finished cleanings, and every one of them
 * rendered an empty cell where the badge goes. Found by looking at it.
 *
 * All muted on purpose. These are the settled rows, and colour in this column
 * means "this wants something from you" - which a finished cleaning does not.
 */
export const CLEANING_STATUS_META: Record<string, { label: string; tone: CleaningTone }> = {
  completed: { label: 'Done', tone: 'secondary' },
  cancelled: { label: 'Cancelled', tone: 'secondary' },
  not_required: { label: 'Not required', tone: 'secondary' },
}

/**
 * The one badge a row's State column shows, whatever kind of row it is.
 *
 * Substate first, because for a live job it is the more specific answer - "No
 * ack" says more than "Scheduled" ever could. A row with neither is a scheduled
 * job admin-api could not classify, and gets nothing rather than a guess.
 */
export function cleaningStateBadge(job: {
  status: string
  substate?: CleaningJob['substate']
}): { label: string; tone: CleaningTone } | null {
  if (job.substate) return CLEANING_SUBSTATE_META[job.substate]
  return CLEANING_STATUS_META[job.status] ?? null
}

/**
 * What the board's status pill says for each job status (CAP-531).
 *
 * Built from the two maps the All cleanings tab already reads, not restated:
 * the same job must read the same on both tabs - "No ack" there is "No ack"
 * here, "Done" is "Done" - and a second copy of the words is how they drift.
 */
export const CLEANING_ITEM_STATUS_META: Record<
  CleaningItemStatus,
  { label: string; tone: CleaningTone }
> = {
  ...CLEANING_SUBSTATE_META,
  completed: CLEANING_STATUS_META.completed,
  cancelled: CLEANING_STATUS_META.cancelled,
  not_required: CLEANING_STATUS_META.not_required,
}

/**
 * The reasons a status pill already says, which would only repeat it as a tag.
 *
 * A job waiting on an ack shows "No ack" on its pill; a "Waiting on an ack" tag
 * beside it adds nothing. Tags are for what the pill cannot carry.
 */
const SAID_BY_STATUS: Partial<Record<CleaningActionKind, CleaningItemStatus>> = {
  block_needs_decision: 'needs_decision',
  assign_team: 'needs_team',
  awaiting_ack: 'needs_ack',
}

/** How each reason reads as a tag, when it says something the status does not. */
const REASON_TAG: Record<CleaningActionKind, { label: string; tone: CleaningTone }> = {
  pinned: { label: 'Pinned', tone: 'secondary' },
  block_needs_decision: { label: 'Needs a decision', tone: 'destructive' },
  assign_team: { label: 'Needs a crew', tone: 'destructive' },
  paying_today_never_acked: { label: 'Pays today', tone: 'destructive' },
  payable_problem: { label: 'No rate', tone: 'destructive' },
  crew_heads_up: { label: 'Changed', tone: 'warning' },
  awaiting_ack: { label: 'No ack', tone: 'warning' },
  completed_but_cancelled: { label: 'Booking cancelled', tone: 'warning' },
}

export interface ReasonTag {
  kind: CleaningActionKind
  label: string
  tone: CleaningTone
  /** The reason's own line, for the tag's accessible description. */
  why: string
  /**
   * What actually changed, in admin-api's words. Only a change carries one that
   * varies per job; every other kind's detail is the generic line in `why`.
   */
  detail: string | null
}

/**
 * The tags one job carries: every reason except those its status already says.
 *
 * Ordered as the reasons are - most urgent first - which is admin-api's order
 * and is not re-ranked here.
 */
export function reasonTags(item: Pick<CleaningActionItem, 'status' | 'reasons'>): ReasonTag[] {
  return item.reasons
    .filter(reason => SAID_BY_STATUS[reason.kind] !== item.status)
    .map(reason => ({
      kind: reason.kind,
      ...REASON_TAG[reason.kind],
      why: CLEANING_KIND_META[reason.kind].why,
      detail: reason.kind === 'crew_heads_up' && reason.detail ? reason.detail : null,
    }))
}

/**
 * Every formatter goes through here, guarded.
 *
 * A property's `timezone` is free-form stored text, and `Intl.DateTimeFormat`
 * THROWS on a zone it does not know rather than ignoring it. Unguarded, one
 * property with a typo'd zone takes the whole board down mid-render - so there
 * is deliberately no second, ungated call site.
 *
 * Built once per (locale, zone, options) and reused: an `Intl.DateTimeFormat`
 * is costly to make, and the portfolio calendar asks for the local day of
 * every deposit event and cleaning across eighteen properties (CAP-1417).
 */
const formatters = new Map<string, Intl.DateTimeFormat>()

function zonedFormatter(
  locale: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat {
  const key = `${locale}|${timeZone}|${JSON.stringify(options)}`
  let formatter = formatters.get(key)
  if (!formatter) {
    try {
      formatter = new Intl.DateTimeFormat(locale, { timeZone, ...options })
    } catch {
      formatter = new Intl.DateTimeFormat(locale, {
        timeZone: DEFAULT_PROPERTY_TIME_ZONE,
        ...options,
      })
    }
    formatters.set(key, formatter)
  }
  return formatter
}

/**
 * "10am", not "10:00 AM".
 *
 * Cleanings land on the hour almost always, so ":00" is four characters of
 * nothing on every row and the column reads faster without it. Minutes still
 * show when there are any - "7:30am" - so a non-standard time cannot be
 * silently rounded into looking standard.
 */
export function shortTime(iso: string | null | undefined, timeZone: string): string | null {
  if (!iso) return null
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return null
  const parts = zonedFormatter('en-US', timeZone, {
    hour: 'numeric',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(ms)
  const at = (type: string) => Number(parts.find(part => part.type === type)?.value)
  const hour24 = at('hour')
  const minute = at('minute')
  if (!Number.isFinite(hour24) || !Number.isFinite(minute)) return null
  const suffix = hour24 < 12 ? 'am' : 'pm'
  const hour = hour24 % 12 === 0 ? 12 : hour24 % 12
  return minute ? `${hour}:${String(minute).padStart(2, '0')}${suffix}` : `${hour}${suffix}`
}

/** "2026-09-14" in the property's own zone, which is the day the crew works. */
export function localDate(iso: string | null | undefined, timeZone: string): string | null {
  if (!iso) return null
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return null
  // en-CA renders as YYYY-MM-DD.
  return zonedFormatter('en-CA', timeZone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(ms)
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * Whether a date needs its year spelled out.
 *
 * One rule, shared: the board reaches back to 2024, and a row from two summers
 * ago reading "Sun 30 Jun" with no year is actively misleading. Two columns
 * spelling the same date differently would be worse, so this lives in one
 * place rather than once per formatter.
 */
function needsYear(date: string, today: string | null | undefined): boolean {
  return !today || date.slice(0, 4) !== today.slice(0, 4)
}

/**
 * The day `offset` days from a "YYYY-MM-DD". Calendar arithmetic, no zone.
 *
 * Exported since CAP-526, which needs the day before the anchor to bound the
 * backwards page. Deliberately zone-free: both sides of that bound are
 * calendar days that admin-api compares against each job's own `local_date` as
 * strings, so introducing an instant here would be the one thing that could
 * put the boundary on the wrong side of midnight.
 */
export function shiftCalendarDate(date: string, offset: number): string {
  const [year, month, day] = date.split('-').map(Number)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return ''
  return new Date(Date.UTC(year, month - 1, day + offset)).toISOString().slice(0, 10)
}

/**
 * How a day is spelled in a table cell.
 *
 * Today, Tomorrow and Yesterday keep their names - those are what an operator
 * calls them, not arithmetic. Everything else is the plain date, with the year
 * only when it is not this one: the board reaches back to 2024, and a row from
 * two summers ago reading "Sun 30 Jun" with no year is actively misleading.
 *
 * There is deliberately NO relative suffix ("6d ago"). The Waiting column
 * already carries how long a row has been sitting there, and saying it twice in
 * adjacent columns is the same redundancy that cost the Detail column.
 */
export function formatCleaningDay(
  date: string | null | undefined,
  today: string | null | undefined
): string {
  if (!date) return 'Date unknown'
  if (today) {
    if (date === today) return 'Today'
    if (date === shiftCalendarDate(today, 1)) return 'Tomorrow'
    if (date === shiftCalendarDate(today, -1)) return 'Yesterday'
  }
  const ms = Date.parse(`${date}T00:00:00Z`)
  if (!Number.isFinite(ms)) return date
  const value = new Date(ms)
  const stem = `${WEEKDAYS[value.getUTCDay()]} ${value.getUTCDate()} ${MONTHS[value.getUTCMonth()]}`
  return needsYear(date, today) ? `${stem} ${date.slice(0, 4)}` : stem
}

/** "15 Sep", or "22 Aug 2027" when the date is not in the current year. */
function shortDay(date: string, today: string | null | undefined): string {
  const ms = Date.parse(`${date}T00:00:00Z`)
  if (!Number.isFinite(ms)) return date
  const value = new Date(ms)
  const stem = `${value.getUTCDate()} ${MONTHS[value.getUTCMonth()]}`
  return needsYear(date, today) ? `${stem} ${date.slice(0, 4)}` : stem
}

/** What produced a cleaning, as an operator would say it. */
export const SOURCE_LABELS: Record<string, string> = {
  reservation: 'Guest booking',
  owner_block: 'Owner block',
  external_block: 'Blocked in Uplisting',
  manual: 'Added by hand',
}

/**
 * The label a board row puts before a block's dates, so an owner's own stay is
 * not read as a guest's (CAP-795). Null for a guest booking, whose dates open
 * the reservation, and for a manual job, which has no stay behind it.
 */
export function blockLabel(sourceKind: string): string | null {
  return sourceKind === 'owner_block' || sourceKind === 'external_block'
    ? SOURCE_LABELS[sourceKind]
    : null
}

/**
 * The stay this cleaning follows: when the departing guests arrived, when they
 * left, and how long they were there.
 *
 * A fortnight's stay is a different job from two nights, which is how the crews
 * have always read a calendar and why the job carries `stay_checkin_at` at all.
 *
 * Nights are counted as CALENDAR days in the property's own zone, never from
 * elapsed milliseconds: a stay spanning the 25-hour night the clocks go back is
 * still two nights, and dividing by 24h would not say so.
 *
 * Returns null rather than a guess when there is no stay behind the job - a
 * manual job, or one derived before CAP-408 (CAP-795 for a block) - because an
 * empty cell is honest and a start date invented from the checkout would not
 * be.
 */
export function formatStay(
  stayCheckInAt: string | null | undefined,
  checkoutAt: string | null | undefined,
  timeZone: string,
  today?: string | null
): string | null {
  const from = localDate(stayCheckInAt, timeZone)
  const to = localDate(checkoutAt, timeZone)
  if (!from || !to) return null

  const fromMs = Date.parse(`${from}T00:00:00Z`)
  const toMs = Date.parse(`${to}T00:00:00Z`)
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return null
  const nights = Math.round((toMs - fromMs) / (24 * 60 * 60 * 1000))
  if (nights <= 0) return `${shortDay(from, today)} → ${shortDay(to, today)}`

  return `${shortDay(from, today)} → ${shortDay(to, today)} · ${nights} ${
    nights === 1 ? 'night' : 'nights'
  }`
}

/**
 * Where a cleaning was BEFORE somebody moved it, spelled for a row.
 *
 * The board files every row under the day the cleaning actually happens, so
 * the destination is already the group header above it - a row under "Thu 24
 * Sep" reading "Moved to 24 Sep" spends a column saying what the reader just
 * read. What the header cannot say is where the job came from, which is the
 * thing an operator scanning the board is missing: this cleaning belongs to
 * Sunday's checkout and is being done on Thursday.
 *
 * So the label names the ORIGIN - the checkout the job was derived from - and
 * leaves the destination to the grouping.
 *
 * Deliberately NOT keyed on `scheduled_at_explicit`. That flag means the time
 * is pinned, which is not the same thing: admin-api sets it when an operator
 * submits the time a job already has - "leave it here", so a later checkout
 * change cannot drag it - and a row reading "Moved from 10am" on a cleaning
 * nobody moved is exactly the constant-in-a-column noise the Time column was
 * removed for.
 *
 * Comparing against `checkout_at` asks the question the label answers. The day
 * comes along when the two fall on different dates; when they do not, only the
 * time moved and only the time is worth printing.
 */
export function formatMovedFrom(
  scheduledAt: string | null | undefined,
  checkoutAt: string | null | undefined,
  timeZone: string,
  today?: string | null
): string | null {
  if (!scheduledAt || !checkoutAt) return null
  const scheduledMs = Date.parse(scheduledAt)
  const checkoutMs = Date.parse(checkoutAt)
  if (!Number.isFinite(scheduledMs) || !Number.isFinite(checkoutMs)) return null
  if (scheduledMs === checkoutMs) return null

  const time = shortTime(checkoutAt, timeZone)
  if (!time) return null

  const scheduledDate = localDate(scheduledAt, timeZone)
  const checkoutDate = localDate(checkoutAt, timeZone)
  if (scheduledDate && checkoutDate && scheduledDate !== checkoutDate) {
    return `Moved from ${shortDay(checkoutDate, today)}, ${time}`
  }
  return `Moved from ${time}`
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/
const TIME_OF_DAY = /^(\d{2}):(\d{2})$/

/**
 * How far a zone is from UTC at a given instant, in milliseconds.
 *
 * Read out of `Intl` rather than a table, which is the only way to be right
 * about a zone's daylight-saving history without shipping one.
 */
function zoneOffsetMs(instantMs: number, timeZone: string): number {
  const parts = zonedFormatter('en-CA', timeZone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instantMs)
  const at = (type: string) => Number(parts.find(part => part.type === type)?.value)
  const asUtc = Date.UTC(
    at('year'),
    at('month') - 1,
    at('day'),
    at('hour'),
    at('minute'),
    at('second')
  )
  return asUtc - instantMs
}

/**
 * A wall-clock date and time in the property's zone, as the instant the API
 * stores.
 *
 * The operator picks "the 15th at 7am" where the house is; `scheduled_at` is
 * an instant. This is the conversion between the two, and it is the same
 * double-offset correction `localTimeToInstant` makes in admin-api: the offset
 * at the naive UTC reading of a wall clock and the offset at the instant it
 * actually maps to differ across a daylight-saving change, so the first answer
 * is re-checked against itself. Taking the offset once puts a cleaning an hour
 * out twice a year, on a job nobody touched again.
 *
 * Returns null rather than a guess on anything that is not a `YYYY-MM-DD` and
 * an `HH:MM`: a half-typed date must not become a real instant.
 */
export function localDateTimeToInstant(
  date: string,
  time: string,
  timeZone: string
): string | null {
  const dateMatch = ISO_DATE.exec(date ?? '')
  const timeMatch = TIME_OF_DAY.exec(time ?? '')
  if (!dateMatch || !timeMatch) return null

  const [, year, month, day] = dateMatch
  const [, hour, minute] = timeMatch
  const naiveUtcMs = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute)
  )
  if (!Number.isFinite(naiveUtcMs)) return null

  const firstOffset = zoneOffsetMs(naiveUtcMs, timeZone)
  let instantMs = naiveUtcMs - firstOffset
  const secondOffset = zoneOffsetMs(instantMs, timeZone)
  if (secondOffset !== firstOffset) instantMs = naiveUtcMs - secondOffset
  return new Date(instantMs).toISOString()
}

/**
 * An instant as "HH:MM" in the property's zone, for a `<input type="time">`.
 *
 * The partner of `localDateTimeToInstant`: together they let a control show
 * where a cleaning currently sits and hand back where it should go. Distinct
 * from `shortTime`, which spells a time for a human to READ ("7:30am") and is
 * not a value any input accepts.
 */
export function localTimeOfDay(iso: string | null | undefined, timeZone: string): string | null {
  if (!iso) return null
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return null
  const parts = zonedFormatter('en-CA', timeZone, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(ms)
  const at = (type: string) => parts.find(part => part.type === type)?.value
  const hour = at('hour')
  const minute = at('minute')
  if (!hour || !minute) return null
  return `${hour}:${minute}`
}

/* -------------------------------------------------------------------------
 * The per-job timeline (CAP-384)
 * ---------------------------------------------------------------------- */

/**
 * Human wording for a write's name.
 *
 * admin-api names events after the write that made them (`job_assign`,
 * `job_derived`), which is right for a log and wrong for a panel Andrei reads
 * at a glance. Anything unmapped falls through to its own name with the
 * `job_` prefix dropped and underscores opened out, so a write added later
 * reads passably without a change here.
 */
const EVENT_LABELS: Record<string, string> = {
  created: 'Created',
  job_created: 'Created',
  job_created_manual: 'Created by hand',
  job_derived: 'Updated from the booking',
  assigned: 'Crew assigned',
  job_assign: 'Crew assigned',
  confirmed: 'Confirmed',
  job_confirm: 'Confirmed',
  acked: 'Crew acknowledged',
  job_ack: 'Acknowledged',
  job_crew_acked: 'Crew acknowledged',
  job_reschedule: 'Rescheduled',
  job_note: 'Note edited',
  job_pin: 'Pinned',
  job_unpin: 'Unpinned',
  job_not_required: 'Marked not required',
  job_undecline: 'Put back on the schedule',
  job_decide: 'Decided',
  job_told_them: 'Crew told',
  job_asked: 'Asked the crew',
  job_notice_sent: 'Change notice sent',
  job_heads_up_emailed: 'Heads-up emailed',
  job_bulk_completed: 'Completed in bulk',
  payable_sent: 'Payable sent',
  pinned: 'Pinned',
}

export function formatHistoryEvent(event: string): string {
  const known = EVENT_LABELS[event]
  if (known) return known
  if (event.startsWith('notice:')) return `Change notice: ${event.slice('notice:'.length)}`
  const words = event.replace(/^job_/, '').replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export interface HistoryActor {
  kind: 'person' | 'process' | 'crew' | 'unknown'
  id: string
  location?: string
}

/**
 * Who did it, in words.
 *
 * The kind is spelled out rather than left implied by the id, because the
 * distinction Andrei asked for is exactly "a process or a person" and an id
 * alone does not carry it - `alesia` is a crew, `cleaning-reconcile` is a cron,
 * and both look like bare names.
 */
export function formatHistoryActor(actor: HistoryActor | undefined): string {
  if (!actor) return 'Actor not recorded'
  switch (actor.kind) {
    case 'person':
      return actor.id
    case 'crew':
      return `${actor.id} (crew)`
    case 'process':
      return `${actor.id} (automatic)`
    default:
      return 'Actor not recorded'
  }
}

export interface HistoryChange {
  field: string
  from: string | null
  to: string | null
}

/** Human names for the fields a write can move. */
const FIELD_LABELS: Record<string, string> = {
  status: 'Status',
  scheduled_at: 'Cleaning time',
  team_id: 'Crew',
  confirmed_at: 'Confirmed',
  acked_at: 'Acknowledged',
  needs_decision: 'Needs a decision',
  not_required_reason: 'Not-required reason',
  note: 'Note',
  pinned_at: 'Pinned',
}

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/

/**
 * A stored value as a human reads it.
 *
 * An instant is spelled in the PROPERTY's zone, like every other time on this
 * screen. Printing the stored UTC string instead was the single worst thing in
 * the panel: an operator comparing a rescheduled time against the one shown two
 * lines above would have been reading two different clocks.
 */
function formatChangeValue(
  value: string | null,
  timeZone: string,
  today: string | null | undefined
): string {
  if (value === null || value === '') return 'none'
  if (value === 'true') return 'yes'
  if (value === 'false') return 'no'
  if (!ISO_INSTANT.test(value)) return value
  const day = formatCleaningDay(localDate(value, timeZone), today)
  const time = shortTime(value, timeZone)
  return time ? `${day} at ${time}` : day
}

/** "Crew: alesia → elisa", with times in the property's own zone. */
export function formatHistoryChange(
  change: HistoryChange,
  timeZone: string,
  today: string | null | undefined
): string {
  const label = FIELD_LABELS[change.field] ?? change.field.replace(/_/g, ' ')
  return `${label}: ${formatChangeValue(change.from, timeZone, today)} → ${formatChangeValue(
    change.to,
    timeZone,
    today
  )}`
}

/**
 * Whether a change is worth a line of its own next to `event`.
 *
 * A first-time stamp on the field the event is NAMED for says nothing the
 * label has not already said - "Confirmed" followed by "Confirmed: none → (a
 * timestamp)" is the panel talking to itself. A change to that same field with
 * a previous value is kept, because that is a correction and the old value is
 * the interesting part.
 */
export function isChangeWorthShowing(change: HistoryChange, event: string): boolean {
  if (change.from !== null) return true
  const named: Record<string, string> = {
    job_confirm: 'confirmed_at',
    confirmed: 'confirmed_at',
    job_ack: 'acked_at',
    job_crew_acked: 'acked_at',
    acked: 'acked_at',
    job_pin: 'pinned_at',
    pinned: 'pinned_at',
  }
  return named[event] !== change.field
}

/**
 * "From external_block 2026-10-08" in words (CAP-810).
 *
 * admin-api writes a derived job's origin as `From <source kind> <source id>`.
 * A block's id is the day it starts; a reservation's is its Uplisting id.
 * Anything that does not match is shown as the API sent it.
 */
export function formatHistoryDetail(detail: string, today: string | null | undefined): string {
  const match = /^From (reservation|external_block|owner_block) (\S+)$/.exec(detail)
  if (!match) return detail
  const [, kind, id] = match
  if (kind === 'reservation') return `From reservation ${id}`
  const block = kind === 'external_block' ? 'the Uplisting block' : 'the owner block'
  return /^\d{4}-\d{2}-\d{2}$/.test(id)
    ? `From ${block} starting ${formatCleaningDay(id, today)}`
    : `From ${block} ${id}`
}
